/* eslint-disable @typescript-eslint/no-unused-vars */
import {
    IonPage,
    IonContent,
    IonLoading,
    IonAlert,
    IonItem,
    IonText,
    useIonViewWillEnter,
} from "@ionic/react";
import axios from "axios";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";

// import { useAuth, useAuthInit } from "../Auth";

import {
    setList,
    requestID,
    baseUrl,
    setUserSession,
    setCategoryList,
    setCategoryNumber,
    setShopList,
    setShopNumber,
    getUser,
} from "../../../common/utils";

import {
    Box,
    Button,
    Container,
    Paper,
    TextField,
    MenuItem,
    Grid,
    Stepper,
    Step,
    StepLabel,
    Typography,
    Stack,
    Chip
} from "@mui/material";
import { CloudUpload, InsertDriveFile } from "@mui/icons-material";

import { Toast } from "@capacitor/toast";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth, useAuthInit } from "../../../Auth";

// --- CUSTOM FILE INPUT COMPONENT ---
interface CustomFileInputProps {
    label: string;
    file: File | null;
    setFile: (file: File | null) => void;
}

const CustomFileInput: React.FC<CustomFileInputProps> = ({ label, file, setFile }) => {
    return (
        <Box
            sx={{
                border: "1px dashed #bdbdbd",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                backgroundColor: "#fafafa",
                "&:hover": {
                    backgroundColor: "#f0f0f0",
                    borderColor: "#1976d2",
                },
            }}
        >
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                {label}
            </Typography>

            {!file ? (
                <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    size="small"
                    sx={{ mt: 1 }}
                >
                    Select File
                    <input
                        type="file"
                        hidden
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                                setFile(e.target.files[0]);
                            }
                        }}
                    />
                </Button>
            ) : (
                <Stack direction="column" alignItems="center" spacing={1}>
                    <Chip
                        icon={<InsertDriveFile />}
                        label={file.name.length > 20 ? file.name.substring(0, 20) + "..." : file.name}
                        onDelete={() => setFile(null)}
                        color="primary"
                        variant="outlined"
                    />
                    <Typography variant="caption" color="textSecondary">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                </Stack>
            )}
        </Box>
    );
};

const steps = ["Personal Info", "Bank Details", "Compliance Uploads"];

const RegisterPage: React.FC = () => {

    const [showToast, setShowToast] = useState(false);
    const showToastBio = async (msg: string) => {
        await Toast.show({ text: msg });
    };

    const [isInternet, setIsInternet] = useState({
        openAlert: false,
        message: "no message",
    });

    const { isLoggedIn } = useAuth();
    const authInitState = useAuthInit();

    // --- STEPPER STATE ---
    const [activeStep, setActiveStep] = useState(0);

    // --- FORM DATA STATE ---
    // Step 1
    const [fname, setFname] = useState("");
    const [lname, setLname] = useState("");
    const [oname, setOname] = useState("");
    const [uname, setUname] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    // Step 2
    const [vendorType, setVendorType] = useState("");
    const [bankName, setBankName] = useState("");
    const [accountNumber, setAccountNumber] = useState("");

    // Step 3 (Files)
    const [complianceFile, setComplianceFile] = useState<File | null>(null);
    const [taxFile, setTaxFile] = useState<File | null>(null);
    const [certificateFile, setCertificateFile] = useState<File | null>(null);
    const [ninFile, setNinFile] = useState<File | null>(null);

    const [status, setStatus] = useState({ loading: false, error: false });
    const history = useHistory();

    const ref = () => {
        if (authInitState.isLoggedIn) {
            if (getUser().loginData.roleID == 4) {
                history.push("/my/buyerDashboard");
                window.location.replace("/my/buyerDashboard");
            } else {
                history.push("/welcome");
                window.location.replace("/welcome");
            }
        }
    };

    // --- VALIDATION SCHEMA ---
    const schema = yup
        .object({
            // Step 1
            fname: yup.string().required("First Name is required."),
            lname: yup.string().required("Last Name is required."),
            uname: yup.string().required("Username is required."),
            phone: yup.string().required("Phone Number is required."),
            email: yup.string().email("Invalid email").required("Email is required."),
            password: yup.string().required("Password is required."),
            confirmPassword: yup
                .string()
                .oneOf([yup.ref("password"), null], "Passwords must match")
                .required("Confirm Password is required."),

            // Step 2
            vendorType: yup.string().required("Vendor Type is required."),
            bankName: yup.string().required("Bank Name is required."),
            accountNumber: yup
                .string()
                .matches(/^[0-9]+$/, "Must be digits only")
                .min(10, "Must be 10 digits")
                .max(10, "Must be 10 digits")
                .required("Account Number is required."),
        })
        .required();

    const {
        register,
        trigger,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    // --- NAVIGATION HANDLERS ---
    const handleNext = async () => {
        let isValid = false;

        if (activeStep === 0) {
            // Validate Step 1 Fields
            isValid = await trigger([
                "fname",
                "lname",
                "uname",
                "email",
                "phone",
                "password",
                "confirmPassword"
            ]);
        } else if (activeStep === 1) {
            // Validate Step 2 Fields
            isValid = await trigger(["bankName", "accountNumber", "vendorType"]);
        } else {
            isValid = true;
        }

        if (isValid) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    // --- SUBMISSION HANDLER ---
    const handleSubmission = () => {
        if (!complianceFile || !taxFile || !certificateFile || !ninFile) {
            showToastBio("Please upload all required documents before submitting.");
            return;
        }

        setStatus({ loading: true, error: false });
        axiosRetry("Geacloud_Vendors/register", 3, 6); // CHANGED: Route is now 'register'
    };

    const axiosRetry = (url, retries, delay) => {
        const formData = new FormData();

        // --- TEXT FIELDS (Mapped to snake_case) ---
        formData.append("email", email);
        formData.append("first_name", fname);
        formData.append("last_name", lname);
        formData.append("username", uname);
        formData.append("phone", phone);
        formData.append("password", password);
        formData.append("vendor_type", vendorType);
        formData.append("bank_name", bankName);
        formData.append("account_number", accountNumber);

        // Optional field
        if(oname) {
            formData.append("other_name", oname);
        }
        console.log("Registration information", axiosRetry)


        // --- FILE UPLOADS (Mapped to specific keys) ---
        if (complianceFile) formData.append("compliance", complianceFile);
        if (taxFile) formData.append("tax", taxFile);
        if (certificateFile) formData.append("certificate", certificateFile);
        if (ninFile) formData.append("nin", ninFile);

        // System/Request ID
        formData.append("requestID", requestID());

        return axios
            .post(baseUrl() + url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((response) => {
                console.log({response})
                if (response.status == true) {
                    showToastBio("Registration Successful");
                    setStatus({ loading: false, error: false });
                } else {
                    setStatus({ loading: false, error: false });
                    setIsInternet({
                        openAlert: true,
                        message: response.data.message || "Registration Failed",
                    });
                }
            })
            .catch(function (error) {
                if (retries > 0 && !error.response) {
                    setTimeout(() => {
                        return axiosRetry(url, retries - 1, delay * 2);
                    }, delay);
                } else {
                    setStatus({ loading: false, error: false });

                    // Enhanced Error Logging
                    if (error.response) {
                        console.log("Server Error Data:", error.response.data);
                        setIsInternet({
                            openAlert: true,
                            message: error.response.data.message || "Server Error",
                        });
                    } else {
                        setIsInternet({
                            openAlert: true,
                            message: "Connection Error: " + error.message,
                        });
                    }

                    return Promise.reject(error);
                }
            });

    };

    useIonViewWillEnter(() => {
        ref();
    });

    return (
        <IonPage className="toolbarxxx">
            {/* <IonAlert
                isOpen={isInternet.openAlert}
                header="Registration - Alert"
                message={`${isInternet.message} `}
                buttons={["Close"]}
                onDidDismiss={() =>
                    setIsInternet({ openAlert: false, message: "no message" })
                }
            /> */}

            <IonAlert
    isOpen={isInternet.openAlert}
    header="Registration - Alert"
    message={`${isInternet.message}`}
    buttons={["Close"]}
    onDidDismiss={() => {
        setIsInternet({ openAlert: false, message: "no message" });
        history.push("/folder/vendors/:name");
    }}
/>

            <IonContent className="ion-padding loginTopBg content-center-login">
                <Box sx={{ mt: "5%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {/* <Typography sx={{ mt: 2, mb: 1, textAlign: "center" }} component="h1" variant="h4">
                        RMF Cooperative Society
                    </Typography> */}
                    <Typography component="h1" variant="subtitle1" sx={{ mb: 3 }}>
                        Vendor Registration
                    </Typography>
                </Box>

                <Container component="main" maxWidth="md">
                    <Paper sx={{ p: 2 }}>

                        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>

                        <Box sx={{ mt: 1, width: "100%" }}>

                            {/* --- STEP 1: PERSONAL INFO --- */}
                            {activeStep === 0 && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="First Name"
                                            {...register("fname")}
                                            value={fname}
                                            error={!!errors.fname}
                                            helperText={errors.fname?.message}
                                            onChange={(e) => setFname(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="Last Name"
                                            {...register("lname")}
                                            value={lname}
                                            error={!!errors.lname}
                                            helperText={errors.lname?.message}
                                            onChange={(e) => setLname(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            margin="dense"
                                            fullWidth
                                            label="Other Name (Optional)"
                                            value={oname}
                                            onChange={(e) => setOname(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    {/* USERNAME */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="Username"
                                            {...register("uname")}
                                            value={uname}
                                            error={!!errors.uname}
                                            helperText={errors.uname?.message}
                                            onChange={(e) => setUname(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="Phone Number"
                                            type="tel"
                                            {...register("phone")}
                                            value={phone}
                                            error={!!errors.phone}
                                            helperText={errors.phone?.message}
                                            onChange={(e) => setPhone(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="Email Address"
                                            type="email"
                                            {...register("email")}
                                            value={email}
                                            error={!!errors.email}
                                            helperText={errors.email?.message}
                                            onChange={(e) => setEmail(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="Password"
                                            type="password"
                                            {...register("password")}
                                            value={password}
                                            error={!!errors.password}
                                            helperText={errors.password?.message}
                                            onChange={(e) => setPassword(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    {/* CONFIRM PASSWORD */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="Confirm Password"
                                            type="password"
                                            {...register("confirmPassword")}
                                            value={confirmPassword}
                                            error={!!errors.confirmPassword}
                                            helperText={errors.confirmPassword?.message}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            {/* --- STEP 2: BANK DETAILS --- */}
                            {activeStep === 1 && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="Vendor Type"
                                            value={vendorType}
                                            error={!!errors.vendorType}
                                            helperText={errors.vendorType?.message}
                                            {...register("vendorType")}
                                            onChange={(e) => setVendorType(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            <MenuItem value="service">Service</MenuItem>
                                            <MenuItem value="product">Product</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="Bank Name"
                                            {...register("bankName")}
                                            value={bankName}
                                            error={!!errors.bankName}
                                            helperText={errors.bankName?.message}
                                            onChange={(e) => setBankName(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={12}>
                                        <TextField
                                            margin="dense"
                                            required
                                            fullWidth
                                            label="Account Number"
                                            type="number"
                                            {...register("accountNumber")}
                                            value={accountNumber}
                                            error={!!errors.accountNumber}
                                            helperText={errors.accountNumber?.message}
                                            onChange={(e) => setAccountNumber(e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                            )}

                            {/* --- STEP 3: FILE UPLOADS --- */}
                            {activeStep === 2 && (
                                <Box>
                                    <Typography variant="body2" color="error" sx={{ mb: 2, textAlign: 'center' }}>
                                        * All documents are required for verification.
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <CustomFileInput
                                                label="Upload Compliance Document"
                                                file={complianceFile}
                                                setFile={setComplianceFile}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CustomFileInput
                                                label="Upload Tax Document"
                                                file={taxFile}
                                                setFile={setTaxFile}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CustomFileInput
                                                label="Upload Certificate"
                                                file={certificateFile}
                                                setFile={setCertificateFile}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <CustomFileInput
                                                label="Upload NIN Slip/ID"
                                                file={ninFile}
                                                setFile={setNinFile}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* --- NAVIGATION BUTTONS --- */}
                            <Box sx={{ display: "flex", flexDirection: "row", pt: 3 }}>
                                <Button
                                    color="inherit"
                                    disabled={activeStep === 0}
                                    onClick={handleBack}
                                    sx={{ mr: 1 }}
                                >
                                    Back
                                </Button>
                                <Box sx={{ flex: "1 1 auto" }} />

                                {activeStep === steps.length - 1 ? (
                                    <Button onClick={handleSubmit(handleSubmission)} variant="contained">
                                        Submit Registration
                                    </Button>
                                ) : (
                                    <Button onClick={handleNext} variant="contained">
                                        Next
                                    </Button>
                                )}
                            </Box>

                            {/* LOGIN LINK (Only on Step 1) */}
                            {/* {activeStep === 0 && (
                                <Box sx={{ mt: 2, textAlign: "center" }}>
                                    <IonItem lines="none" detail={false} routerLink="/login">
                                        <IonText className="zillythemex" style={{ width: '100%', textAlign: 'center' }}>
                                            Already have an account? Login
                                        </IonText>
                                    </IonItem>
                                </Box>
                            )} */}

                        </Box>

                        <IonLoading
                            message="Processing Registration..."
                            spinner="circles"
                            isOpen={status.loading}
                        />
                    </Paper>
                </Container>
            </IonContent>
        </IonPage>
    );
};

export default RegisterPage;